import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button, Badge, Table, Modal, Input, Select } from '@/components/ui';
import { resourceService } from '@/services/resourceService';
import type { Resource, ResourceType, CreateResourceRequest } from '@/types';
import styles from './ResourcesPage.module.css';

const RESOURCE_TYPE_OPTIONS = [
  { value: 'DOCTOR', label: 'Médico' },
  { value: 'ROOM', label: 'Sala' },
  { value: 'EQUIPMENT', label: 'Equipamento' },
];

const FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  ...RESOURCE_TYPE_OPTIONS,
];

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  DOCTOR: 'Médico',
  ROOM: 'Sala',
  EQUIPMENT: 'Equipamento',
};

const RESOURCE_TYPE_BADGE_VARIANT: Record<ResourceType, 'info' | 'warning' | 'neutral'> = {
  DOCTOR: 'info',
  ROOM: 'warning',
  EQUIPMENT: 'neutral',
};

/**
 * Admin page for listing and managing resources (doctors, rooms, equipment).
 *
 * Features:
 * - Filterable resource list by type
 * - Create new resource via modal
 * - Edit resource via modal
 * - Toggle active/inactive status
 * - Click row to navigate to resource detail page
 */
export function ResourcesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [typeFilter, setTypeFilter] = useState<ResourceType | ''>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<ResourceType>('DOCTOR');
  const [formError, setFormError] = useState<string | null>(null);

  const queryParams = typeFilter ? { resourceType: typeFilter } : undefined;

  const {
    data: resources = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['resources', queryParams],
    queryFn: () => resourceService.getResources(queryParams),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateResourceRequest) => resourceService.createResource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      closeCreateModal();
    },
    onError: () => {
      setFormError('Erro ao criar recurso. Tente novamente.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateResourceRequest }) =>
      resourceService.updateResource(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      closeEditModal();
    },
    onError: () => {
      setFormError('Erro ao atualizar recurso. Tente novamente.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (resource: Resource) =>
      resource.isActive
        ? resourceService.deactivateResource(resource.id)
        : resourceService.activateResource(resource.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });

  const openCreateModal = useCallback(() => {
    setFormName('');
    setFormType('DOCTOR');
    setFormError(null);
    setIsCreateModalOpen(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormError(null);
  }, []);

  const openEditModal = useCallback((resource: Resource) => {
    setFormName(resource.name);
    setFormType(resource.resourceType);
    setFormError(null);
    setEditingResource(resource);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingResource(null);
    setFormError(null);
  }, []);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!formName.trim()) {
        setFormError('Nome é obrigatório.');
        return;
      }
      createMutation.mutate({ name: formName.trim(), resourceType: formType });
    },
    [formName, formType, createMutation],
  );

  const handleUpdate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!editingResource) return;
      if (!formName.trim()) {
        setFormError('Nome é obrigatório.');
        return;
      }
      updateMutation.mutate({
        id: editingResource.id,
        data: { name: formName.trim(), resourceType: formType },
      });
    },
    [formName, formType, editingResource, updateMutation],
  );

  const handleToggleActive = useCallback(
    (event: React.MouseEvent, resource: Resource) => {
      event.stopPropagation();
      toggleActiveMutation.mutate(resource);
    },
    [toggleActiveMutation],
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent, resource: Resource) => {
      event.stopPropagation();
      openEditModal(resource);
    },
    [openEditModal],
  );

  const handleRowClick = useCallback(
    (resource: Resource) => {
      navigate(`/admin/resources/${resource.id}`);
    },
    [navigate],
  );

  const columns = [
    {
      key: 'name',
      header: 'Nome',
      render: (resource: Resource) => (
        <span className={styles.resourceName}>{resource.name}</span>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      width: '140px',
      render: (resource: Resource) => (
        <Badge variant={RESOURCE_TYPE_BADGE_VARIANT[resource.resourceType]}>
          {RESOURCE_TYPE_LABELS[resource.resourceType]}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (resource: Resource) => (
        <Badge variant={resource.isActive ? 'success' : 'error'}>
          {resource.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '200px',
      render: (resource: Resource) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleEditClick(e, resource)}
          >
            Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleToggleActive(e, resource)}
            disabled={toggleActiveMutation.isPending}
          >
            {resource.isActive ? 'Desativar' : 'Ativar'}
          </Button>
        </div>
      ),
    },
  ];

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar recursos. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Recursos</h1>
          <p className={styles.subtitle}>
            Gerencie médicos, salas e equipamentos da clínica.
          </p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
          Novo Recurso
        </Button>
      </header>

      <div className={styles.filters}>
        <Select
          label="Filtrar por tipo"
          options={FILTER_OPTIONS}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ResourceType | '')}
        />
      </div>

      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando recursos...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={resources}
          keyExtractor={(resource) => resource.id}
          onRowClick={handleRowClick}
          emptyMessage="Nenhum recurso encontrado."
        />
      )}

      {/* Create modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Novo Recurso"
        size="sm"
      >
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          <Input
            label="Nome"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Nome do recurso"
            required
            fullWidth
          />
          <Select
            label="Tipo"
            options={RESOURCE_TYPE_OPTIONS}
            value={formType}
            onChange={(e) => setFormType(e.target.value as ResourceType)}
            fullWidth
          />
          {formError && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Criar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editingResource !== null}
        onClose={closeEditModal}
        title="Editar Recurso"
        size="sm"
      >
        <form onSubmit={handleUpdate} className={styles.form} noValidate>
          <Input
            label="Nome"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Nome do recurso"
            required
            fullWidth
          />
          <Select
            label="Tipo"
            options={RESOURCE_TYPE_OPTIONS}
            value={formType}
            onChange={(e) => setFormType(e.target.value as ResourceType)}
            fullWidth
          />
          {formError && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeEditModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
